import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  type PublishFormTemplateDto,
  type PublishProcessTemplateDto,
  type UseFormTemplateDto,
  type UseProcessTemplateDto,
  templateService,
} from "@/services/template";

export function useGetAllFormTemplates() {
  return useQuery({
    queryKey: ["templates", "forms"],
    queryFn: () => templateService.getAllFormTemplates(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useGetAllProcessTemplates() {
  return useQuery({
    queryKey: ["templates", "processes"],
    queryFn: () => templateService.getAllProcessTemplates(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUseFormTemplate() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UseFormTemplateDto }) =>
      templateService.useFormTemplate(id, data),
    onSuccess: (response) => {
      toast.success(t("templates.formCreatedFromTemplate"));
      queryClient.invalidateQueries({ queryKey: ["forms", "all"] });
      queryClient.invalidateQueries({ queryKey: ["folders"] });
      if (response?.id) {
        navigate(`/admin/form/design/${response.id}`);
      }
    },
    onError: () => toast.error(t("templates.useTemplateFailed")),
  });
}

export function useUseProcessTemplate() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UseProcessTemplateDto }) =>
      templateService.useProcessTemplate(id, data),
    onSuccess: (response) => {
      toast.success(t("templates.processCreatedFromTemplate"));
      queryClient.invalidateQueries({ queryKey: ["processes"] });
      if (response?.processId) {
        navigate(`/admin/process/process-design/${response.processId}`);
      }
    },
    onError: () => toast.error(t("templates.useTemplateFailed")),
  });
}

export function usePublishFormTemplate() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: PublishFormTemplateDto) =>
      templateService.publishFormTemplate(data),
    onSuccess: () => {
      toast.success(t("templates.formPublishedAsTemplate"));
      queryClient.invalidateQueries({ queryKey: ["templates", "forms"] });
    },
    onError: () => toast.error(t("templates.publishFailed")),
  });
}

export function usePublishProcessTemplate() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: PublishProcessTemplateDto) =>
      templateService.publishProcessTemplate(data),
    onSuccess: () => {
      toast.success(t("templates.processPublishedAsTemplate"));
      queryClient.invalidateQueries({ queryKey: ["templates", "processes"] });
    },
    onError: () => toast.error(t("templates.publishFailed")),
  });
}

export function useDeleteFormTemplate() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: string) => templateService.deleteFormTemplate(id),
    onSuccess: () => {
      toast.success(t("templates.formTemplateDeleted"));
      queryClient.invalidateQueries({ queryKey: ["templates", "forms"] });
    },
    onError: () => toast.error(t("templates.deleteFailed")),
  });
}

export function useDeleteProcessTemplate() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: string) => templateService.deleteProcessTemplate(id),
    onSuccess: () => {
      toast.success(t("templates.processTemplateDeleted"));
      queryClient.invalidateQueries({ queryKey: ["templates", "processes"] });
    },
    onError: () => toast.error(t("templates.deleteFailed")),
  });
}
